// What a SUPPLIER is told about whether the trader can see their quote.
//
// Owner, 2026-10-11, when direct requests were opened to every operational
// supplier (20261011224335_request_open_pool): an unverified supplier can now
// find and quote on any open direct request, and that quote is saved but held
// from the trader until their account is approved (golden rule 2). Until this
// file, nothing on the supplier side said so — the quote simply went quiet.
//
// ⚠️ MIRRORS SQL, AND THE SQL IS THE AUTHORITY:
//   · QUOTE_VISIBLE_SUPPLIER_STATUSES is the list guard_offer_write tests
//     (`lower(status) in ('verified','approved','active')`) to decide, at
//     insert, between buyer_visible and admin_only/unverified_supplier.
//   · isQuoteHeldForVerification is isHeldForVerification from
//     offerVisibility.js — the same predicate the admin board counts with —
//     plus "still pending", because an accepted or rejected quote is past the
//     point where visibility is the supplier's question.
// If guard_offer_write's list changes, this list changes with it.
//
// ═══ CHANGED 2026-10-12 — a claimed catalogue factory also reaches traders ═══
// Owner: «اي مصنع من تملك صفحته يوصل عرضه». The server predicate is now
// supplier_quotes_reach_traders() (migrations 20261012010959…011457):
// verified-equivalent status, OR the supplier claimed a factory page. A claim
// proves the phone/email on file, and does NOT change profiles.status — so the
// status alone can no longer answer this question. The client asks the server
// (rpc my_quotes_reach_traders) and passes the answer as `serverAnswer`; the
// status list is only the fallback while that answer is loading or failed.
//
// Positive test (golden rule 12): it names the statuses whose quotes ARE
// visible. A status invented later reads as "not yet visible", which shows the
// note — the safe direction, since the note only ever invites verification.

import { isHeldForVerification } from './offerVisibility';

export const QUOTE_VISIBLE_SUPPLIER_STATUSES = Object.freeze(['verified', 'approved', 'active']);

/**
 * True when a quote this supplier submits now goes straight to the trader.
 * `serverAnswer` is my_quotes_reach_traders() — authoritative when it is a
 * boolean; until then the status list decides (a claimed factory briefly sees
 * the note, never the reverse).
 */
export function supplierQuotesReachTraders(status, serverAnswer = null) {
  if (typeof serverAnswer === 'boolean') return serverAnswer;
  return QUOTE_VISIBLE_SUPPLIER_STATUSES.includes(String(status ?? '').toLowerCase());
}

/** Asks the server. Resolves to a boolean, or null when it cannot answer. */
export async function fetchMyQuotesReachTraders(sb) {
  if (!sb) return null;
  const { data, error } = await sb.rpc('my_quotes_reach_traders');
  if (error) {
    console.error('my_quotes_reach_traders failed:', error.message);
    return null;
  }
  return typeof data === 'boolean' ? data : null;
}

/**
 * One of the supplier's own quotes is saved but hidden from the trader, and
 * verifying the account is what releases it. `request` is the offer's request
 * embed: a quote on a curated (managed) request is never released by approval,
 * so it must not be told it will be.
 */
export function isQuoteHeldForVerification(offer, request = null) {
  return String(offer?.status ?? '').toLowerCase() === 'pending'
    && isHeldForVerification(offer, request);
}

const COPY = {
  // Above the submit button, before a quote is sent.
  beforeSubmit: {
    ar: 'حسابك لم يُوثَّق بعد: سيُحفظ عرضك، لكن التاجر لن يراه حتى نعتمد حسابك. ارفع الرخصة التجارية لتسريع الاعتماد.',
    en: "Your account isn't verified yet: your quote will be saved, but the trader won't see it until we approve your account. Upload your business licence to speed this up.",
    zh: '您的账户尚未认证：报价会被保存，但在我们审核通过您的账户之前，买家看不到它。请上传营业执照以加快审核。',
  },
  // The toast once the quote is saved.
  savedHeld: {
    ar: '✓ تم حفظ عرضك — يظهر للتاجر بعد توثيق حسابك',
    en: '✓ Quote saved — the trader sees it once your account is verified',
    zh: '✓ 报价已保存 — 账户认证后买家即可看到',
  },
  // The chip on a held quote in "My offers".
  chip: {
    ar: 'مخفي عن التاجر حتى التوثيق',
    en: 'Hidden from trader until verified',
    zh: '认证前买家不可见',
  },
  // The line under that chip.
  held: {
    ar: 'عرضك محفوظ، ويظهر للتاجر تلقائياً بعد اعتماد حسابك.',
    en: 'Your quote is saved and reaches the trader automatically once your account is approved.',
    zh: '您的报价已保存，账户审核通过后将自动展示给买家。',
  },
  cta: {
    ar: 'أكمل التوثيق',
    en: 'Complete verification',
    zh: '完成认证',
  },
};

/** Trilingual copy; Arabic, English and Chinese are all first-class (golden rule 3). */
export function heldQuoteCopy(key, lang = 'en') {
  const entry = COPY[key];
  if (!entry) return '';
  return entry[lang] || entry.en;
}
